import type { TableColumnType } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { apiTabelaPrecoConsultarQueryOptions } from '@/features/tabela-preco/api/apiTabelaPrecoConsultar';
import { numberToCurrency, numberToString } from '@/std/format';
import type { Produto } from '../../types/Produto';
import type { ProdutoPacoteBarra } from '../../types/ProdutoPacoteBarra';
import type { ProdutoUnidade } from '../../types/ProdutoUnidade';
import { useProdutoUnidadeGetQuery } from '../../api/apiProdutoUnidadeGet';
import { calcularPrecoVendaProduto } from '../../util/preco';
import { ProdutoSelectTable } from './ProdutoSelectTable';

type TableData = {
    key: number;
    descricao: string;
    unidade: string;
    multiplicador: number;
    codigoBarras: string;
    preco: number;
    pacoteBarra: ProdutoPacoteBarra | null;
};

const columns: TableColumnType<TableData>[] = [
    {
        dataIndex: 'descricao',
        title: 'Descrição',
    },
    {
        align: 'center',
        dataIndex: 'unidade',
        title: 'Unidade',
    },
    {
        align: 'right',
        dataIndex: 'multiplicador',
        render: (value) => numberToString(value),
        title: 'Multiplicador',
    },
    {
        align: 'center',
        dataIndex: 'codigoBarras',
        title: 'Código de barras',
    },
    {
        align: 'right',
        dataIndex: 'preco',
        render: (value) => numberToCurrency(value),
        title: 'Preço',
    },
];

function useData({
    produto,
    tabelaPrecoIdpk,
}: {
    produto: Produto | null;
    tabelaPrecoIdpk: number | undefined;
}): TableData[] {
    const { data: dataProdutoUnidade } = useProdutoUnidadeGetQuery();

    const { data: dataTabelaPreco } = useQuery({
        ...apiTabelaPrecoConsultarQueryOptions({ idpk: tabelaPrecoIdpk ?? 0 }),
        enabled: !!tabelaPrecoIdpk,
    });

    const tabelaPreco = dataTabelaPreco?.registros[0] || null;

    if (!produto) {
        return [];
    }

    const result: TableData[] = [
        {
            codigoBarras: produto.pro_cod_barra || '',
            descricao: produto.pro_descricao,
            key: 0,
            multiplicador: 1,
            pacoteBarra: null,
            preco: calcularPrecoVendaProduto({ pacoteBarra: null, produto, tabelaPreco }),
            unidade: produto.produto_unidade?.pru_sigla || 'UN',
        },
    ];

    if (!produto.produto_pacote_barra || produto.produto_pacote_barra.length === 0) {
        return result;
    }

    const unidadesById = new Map<number, ProdutoUnidade>();

    for (const unidade of dataProdutoUnidade?.registros ?? []) {
        if (!unidadesById.has(unidade.pru_idpk)) {
            unidadesById.set(unidade.pru_idpk, unidade);
        }
    }

    for (const pacoteBarra of produto.produto_pacote_barra) {
        const unidade = unidadesById.get(pacoteBarra.pcb_produto_unidade_idpk)?.pru_sigla;

        result.push({
            codigoBarras: pacoteBarra.pcb_cod_barra || '',
            descricao: pacoteBarra.pcb_descricao || '',
            key: pacoteBarra.pcb_idpk,
            multiplicador: pacoteBarra.pcb_multiplicador ?? 0,
            pacoteBarra: pacoteBarra,
            preco: calcularPrecoVendaProduto({ pacoteBarra, produto, tabelaPreco }),
            unidade: unidade || 'UN',
        });
    }

    return result;
}

export function ProdutoSelectModalPacote({
    produto,
    tabelaPrecoIdpk,
    onSelect,
    onFocusChange,
}: {
    produto: Produto;
    tabelaPrecoIdpk?: number;
    onSelect: (pacoteBarra: ProdutoPacoteBarra | null) => void;
    onFocusChange?: (pacoteBarra: ProdutoPacoteBarra | null) => void;
}) {
    const data = useData({ produto, tabelaPrecoIdpk });

    return (
        <ProdutoSelectTable
            columns={columns}
            data={data}
            onFocusChange={(record) => onFocusChange?.(record?.pacoteBarra ?? null)}
            onSelect={(record) => onSelect(record.pacoteBarra)}
            rowKey={(record) => record.key}
        />
    );
}
